/**
 * Teams Hub Utilities
 * Re-exports shared functions from @quotech/ui — Teams-specific helpers below.
 */
import { formatDistanceToNow, format, isToday, isYesterday } from 'date-fns'

export { cn, truncate, getInitials } from '@quotech/ui'

export function formatMessageTime(dateStr: string): string {
  const date = new Date(dateStr)
  if (isToday(date)) return format(date, 'HH:mm')
  if (isYesterday(date)) return `Yesterday ${format(date, 'HH:mm')}`
  return format(date, 'MMM d, HH:mm')
}

export function formatRelativeTime(dateStr: string): string {
  return formatDistanceToNow(new Date(dateStr), { addSuffix: true })
}

const AVATAR_COLORS = [
  'from-violet-500 to-purple-600',
  'from-blue-500 to-indigo-600',
  'from-teal-500 to-emerald-600',
  'from-amber-500 to-orange-600',
  'from-rose-500 to-pink-600',
  'from-cyan-500 to-blue-600',
  'from-fuchsia-500 to-purple-600',
  'from-lime-500 to-green-600',
]

export function getAvatarColor(name: string, fallbackIdx = 0): string {
  if (!name) return AVATAR_COLORS[fallbackIdx % AVATAR_COLORS.length]
  const hash = name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0)
  return AVATAR_COLORS[hash % AVATAR_COLORS.length]
}

export function parseMentions(text: string): { text: string; mentions: string[] } {
  const mentionRegex = /@(\w+)/g
  const mentions: string[] = []
  const processedText = text.replace(mentionRegex, (match, username) => {
    mentions.push(username)
    return match
  })
  return { text: processedText, mentions }
}

export function getPresenceConfig(status: string) {
  switch (status) {
    case 'online':
      return { color: 'bg-emerald-500', label: 'Online', ring: 'ring-emerald-200' }
    case 'busy':
      return { color: 'bg-red-500', label: 'Busy', ring: 'ring-red-200' }
    case 'away':
      return { color: 'bg-amber-500', label: 'Away', ring: 'ring-amber-200' }
    default:
      return { color: 'bg-slate-400', label: 'Offline', ring: 'ring-slate-200' }
  }
}

export function groupMessagesByDate<T extends { created_at: string }>(messages: T[]): Map<string, T[]> {
  const groups = new Map<string, T[]>()
  for (const msg of messages) {
    const date = new Date(msg.created_at)
    let key: string
    if (isToday(date)) key = 'Today'
    else if (isYesterday(date)) key = 'Yesterday'
    else key = format(date, 'EEEE, MMMM d')
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key)!.push(msg)
  }
  return groups
}
